/** Room participation is an explicit capability, scoped to one agent membership. */
import { z } from 'zod'
import { IsoTimestamp, Uuid } from '../primitives.js'

export const RoomAgentPolicy = z
  .object({
    enabled: z.boolean(),
    respondTo: z.enum(['mentions', 'mentions_and_replies', 'all_user_messages']),
    proposals: z
      .array(z.enum(['watch', 'research']))
      .max(2)
      .refine((x) => new Set(x).size === x.length, 'unique proposal kinds'),
    dailyCreditsMicro: z
      .string()
      .regex(/^[1-9][0-9]{0,9}$/)
      .refine((x) => /^[1-9][0-9]{0,9}$/.test(x) && BigInt(x) <= 1_000_000_000n, 'at most 1000 credits per day'),
    maxRepliesPerHour: z.number().int().min(1).max(20),
  })
  .strict()
export type RoomAgentPolicy = z.output<typeof RoomAgentPolicy>
export const DEFAULT_ROOM_AGENT_POLICY: RoomAgentPolicy = { enabled: false, respondTo: 'mentions_and_replies', proposals: [], dailyCreditsMicro: '10000000', maxRepliesPerHour: 6 }
export const RoomAgentParticipation = z.object({ policy: RoomAgentPolicy, revision: z.number().int().positive(), payerUserId: Uuid })
export type RoomAgentParticipation = z.output<typeof RoomAgentParticipation>
export const RoomAgentProposalDraft = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('research'), text: z.string().trim().min(8).max(1000) }).strict(),
  z
    .object({
      kind: z.literal('watch'),
      text: z.string().trim().min(8).max(1000),
      intervalMinutes: z.number().int().min(30).max(1440),
      durationHours: z.number().int().min(1).max(168),
    })
    .strict(),
])
export type RoomAgentProposalDraft = z.output<typeof RoomAgentProposalDraft>
export const RoomAgentProposal = z.object({
  id: Uuid,
  roomId: Uuid,
  agentId: Uuid,
  kind: z.enum(['watch', 'research']),
  text: z.string(),
  draft: RoomAgentProposalDraft,
  state: z.enum(['pending', 'accepted', 'rejected', 'expired']),
  expiresAt: IsoTimestamp,
  threadId: Uuid.nullable(),
  watchId: Uuid.nullable(),
})
export type RoomAgentProposal = z.output<typeof RoomAgentProposal>
export const RoomAgentWatch = z.object({
  id: Uuid,
  roomId: Uuid,
  agentId: Uuid,
  text: z.string(),
  intervalMinutes: z.number().int(),
  expiresAt: IsoTimestamp,
  nextRunAt: IsoTimestamp,
  stoppedAt: IsoTimestamp.nullable(),
})
export type RoomAgentWatch = z.output<typeof RoomAgentWatch>
export const RoomAgentProposalParams = z.object({ id: Uuid, proposalId: Uuid })
export const RoomAgentWatchParams = z.object({ id: Uuid, watchId: Uuid })
